import Loader from '@/components/loader/loader-component';
import PropTypes from 'prop-types';
import { Fragment, useCallback, useEffect, useState } from 'react';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import {
  FiAlertTriangle,
  FiChevronDown,
  FiChevronUp,
  FiFileText,
} from 'react-icons/fi';
import { useExpanded, useSortBy, useTable } from 'react-table';
import { useTheme } from 'styled-components';
import Swal from 'sweetalert2';
import { v4 as uuidv4 } from 'uuid';
import Pagination from './components/pagination/pagination-component';
import TableHeader from './components/table-header/table-header-component';
import {
  Column,
  ColumnHeader,
  Main,
  Row,
  RowHeader,
  StyledTable,
  TableContainer,
  TableFilterIcons,
  TableIcons,
  TableTitle,
  TableWrapper,
  Text,
} from './table-style';
import { downloadXLSX, refetchData } from './utils/table-functions';
import ContextMenuViewEditDelete from './components/view-edit-delete/context-menu-view-edit-delete';

/**
 * Table component for generic use in project.
 *
 * @param {object} props - The component props.
 * @param {Function} props.renderRowSubComponent - Function to call when the row is expanded.
 * @param {Array} props.tableHeader - Array of table headers.
 * @param {Array} props.columns - Array of table columns.
 * @param {Array} props.data - Array of table data.
 * @param {boolean} props.loading - Loading state of the table.
 * @param {boolean} props.error - Error state of the table.
 * @param {Function} props.refetch - Function to call when the table needs to be refetched.
 * @param {string} props.dataCount - The total number of data rows that there are.
 * @param {Array} props.pageSizes - Page sizes to display on the table.
 * @param {number} props.defaultPageSize - Default page size of the table.
 * @param {Function} props.onRowClick - Function to call when the row is clicked.
 * @param {string} props.tableTitle - Title of the table.
 * @param {Array} props.tableOptions - Options to display on the table.
 * @param {Array} props.filters - Filters to display on the table.
 * @param {string} props.maxHeight - MaxHeight of table.
 * @param {object} props.defaultVariables - Default variables to use when querying the data.
 * @param {Function} props.getDownloadData - Function to call when the table needs to be downloaded.
 * @param {string} props.fileName - Name of the file to download.
 * @param {Function} props.setLoading - Function to call when the table needs to be set to loading.
 * @param {Function} props.setError - Function to call when the table needs to be set to error.
 * @param {object} props.rowStyle - Style of the row.
 * @param {Function} props.conditionRowStyle - Function to call when the row needs to be styled conditionally.
 * @param {string} props.id - Id of the table.
 * @param {object} props.bodyStyle - Style of the body.
 * @param {Function} props.customClear - Function to call when the table needs to be cleared.
 * @param {string} props.defaultSort - Default sort of the table.
 * @param {boolean} props.emailExport - Flag if the table should be exported via email.
 * @param {boolean} props.modalExport - Flag if the table should be exported via a modal.
 * @param {boolean} props.isPaginationVisible - Flag if the pagination should be visible.
 * @param {JSX.Element} props.tableHeaderProps - Elements added to header
 * @param {Array}props.subRowSameHeaders - sub row headers
 * @param {JSX.Element} props.additionalHeaderButtons - additional header buttons
 * @param {string} props.feature - feature of the table.
 * @param {Function} props.onRowEdit - Function to call when the row is edited.
 * @param {Function} props.onRowDelete - Function to call when the row is deleted.
 * @param {Function} props.onRowDuplicate - Function to call when the row is duplicated.
 * @param {Function} props.onRowView - Function to call when the row is viewed.
 * @param {Function} props.onRowPreview - Function to call when the row is previewed.
 * @param {string} props.previewText - text to display in the preview button.
 * @param {string} props.viewText - text to display in the view button.
 * @param {boolean} props.clearDefaultOnReset - Flag if the default variables should be cleared on reset.
 * @returns {JSX.Element} The Table component.
 */
const Table = ({
  renderRowSubComponent,
  tableHeader,
  columns,
  data,
  loading,
  error,
  refetch,
  dataCount,
  pageSizes,
  defaultPageSize,
  onRowClick,
  tableTitle,
  tableOptions,
  tableHeaderProps,
  filters,
  maxHeight,
  defaultVariables,
  getDownloadData,
  fileName,
  setLoading,
  setError,
  rowStyle,
  conditionRowStyle,
  id: tableId,
  bodyStyle,
  customClear,
  defaultSort,
  emailExport,
  modalExport,
  isPaginationVisible,
  subRowSameHeaders,
  additionalHeaderButtons,
  feature,
  onRowEdit,
  onRowDelete,
  onRowDuplicate,
  onRowView,
  onRowPreview,
  previewText,
  viewText,
  clearDefaultOnReset,
}) => {
  const theme = useTheme();

  const columnStyle = { textAlign: 'center', padding: '25px 0' };
  const columnPaddingStyle = { textAlign: 'center', padding: '60px 0' };

  // USE STATES
  const [selectedRow, setSelectedRow] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [busyDownloading, setBusyDownloading] = useState(false);
  const [variables, setVariables] = useState(defaultVariables);
  const [downloadData, setDownloadData] = useState({
    called: false,
    loading: true,
    data: undefined,
  });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [fetchingPresets, setFetchingPresets] = useState(true);
  const [filterValues, setFilterValues] = useState([]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setHiddenColumns,
    setSortBy,
    state: { sortBy },
  } = useTable(
    {
      columns,
      data,
      manualSortBy: true,
      initialState: defaultSort && {
        sortBy: [
          {
            id: defaultSort.split(':')[0],
            desc: defaultSort.split(':')[1] === 'DESC',
            asc: defaultSort.split(':')[1] === 'ASC',
          },
        ],
        pageSize: defaultPageSize,
        pageIndex,
      },
    },
    useSortBy,
    useExpanded,
  );

  const calculateHorizontalPosition = (buttonRect, estimatedMenuWidth) => {
    const spaceOnRight = window.innerWidth - buttonRect.right;
    const spaceOnLeft = buttonRect.left;

    if (spaceOnRight >= estimatedMenuWidth) {
      return { alignment: 'left', x: buttonRect.x };
    }
    if (spaceOnLeft >= estimatedMenuWidth) {
      return {
        alignment: 'left',
        x: window.innerWidth - estimatedMenuWidth - 50,
      };
    }
    // Fallback: position to the right but allow overflow
    return { alignment: 'right', x: buttonRect.x };
  };

  const calculateVerticalPosition = (buttonRect, estimatedMenuHeight) => {
    const spaceBelow = window.innerHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    if (spaceBelow >= estimatedMenuHeight) {
      return { y: buttonRect.bottom - 30, verticalPlacement: 'bottom' };
    }
    if (spaceAbove >= estimatedMenuHeight) {
      return { y: buttonRect.top + 30, verticalPlacement: 'top' };
    }
    // Not enough space either way - default to bottom and allow overflow
    return { y: buttonRect.bottom - 30, verticalPlacement: 'bottom' };
  };

  const download = useCallback(async () => {
    const controller = new AbortController();
    const tablePreferences = localStorage.getItem('tablePreferences');
    const tableConfigs = JSON.parse(tablePreferences)[tableId];
    const query = tableConfigs.query.filter(
      (q) => !(q.includes('limit') || q.includes('skip')),
    );
    if (emailExport) {
      Swal.fire({
        title: 'Please wait',
        text: 'Sending export to you',
        didOpen: () => {
          Swal.showLoading();
          getDownloadData(query.join('&'), controller.signal)
            .then(() => {
              Swal.close();
              Swal.fire(
                'Success',
                'We emailed your export to your inbox',
                'success',
              );
            })
            .catch(() => {
              Swal.close();
              Swal.fire(
                'Failed to send email',
                'An issue occurred trying to send you the export',
                'error',
              );
            });
        },
      });
    } else if (modalExport) {
      getDownloadData();
    } else {
      setDownloadData({
        called: true,
        loading: true,
        data: undefined,
      });
      getDownloadData(query.join('&'), controller.signal)
        .then((res) =>
          setDownloadData({
            called: true,
            loading: false,
            data: res.data,
          }),
        )
        .catch(() =>
          setDownloadData({
            called: true,
            loading: false,
            data: undefined,
            error: true,
          }),
        );
    }
    return () => {
      controller.abort();
    };
  }, [getDownloadData, tableId, emailExport, modalExport]);

  const getExportArray = useCallback(() => {
    // Converting json into a string array.
    let headers = [];
    const col = [];

    const { data: dataAccessor } = downloadData;

    if (dataAccessor.length > 0) {
      headers = Object.keys(dataAccessor[0]);
      dataAccessor.forEach((d) => {
        const row = [];
        headers.forEach((h) => {
          row.push(d[h]);
        });
        col.push(row);
      });
    }
    return [headers, ...col];
  }, [downloadData]);

  const startDownload = useCallback(() => {
    const exportArray = getExportArray();
    downloadXLSX(exportArray, fileName || tableId, () => {
      setBusyDownloading(false);
    });
  }, [fileName, tableId, getExportArray]);

  const getTablePresets = useCallback(() => {
    const tablePreferences = localStorage.getItem('tablePreferences');
    if (tablePreferences) {
      const tableConfigs = JSON.parse(tablePreferences)[tableId];

      if (tableConfigs) {
        const { query } = tableConfigs;

        setVariables((prev) => {
          const newVariables = { ...prev };

          // Get cached search value and apply it
          const searchQuery = query?.filter((q) => q.includes('search='));
          if (searchQuery?.length > 0) {
            const tempSearch = searchQuery[0].replace('search=', '');
            if (tempSearch !== variables.search) {
              newVariables.search = tempSearch;
            }
          }

          // Get cached sort value and apply it
          const sortQuery = query?.filter((q) => q.includes('sort='));
          if (sortQuery.length > 0) {
            const tempSort = sortQuery[0].replace('sort=', '');

            setSortBy([
              {
                id: tempSort.split(':')[0],
                desc: tempSort.split(':')[1] === 'DESC',
                asc: tempSort.split(':')[1] === 'ASC',
              },
            ]);

            newVariables.sort = tempSort;
          }

          // Get cached filter values and apply it
          const filterQuery = query?.filter((q) => q.includes('filter='));
          if (filterQuery?.length > 0) {
            filterQuery.forEach((f) => {
              const tempFilter = f.replace('filter=', '');
              if (tempFilter.includes('$') && tempFilter.includes('=')) {
                const filterKey = tempFilter.split('$')[0];
                const filterValue = tempFilter.split('=')[1];
                const filterType = tempFilter.split('$')[1].split('=')[0];
                setFilterValues({
                  ...filterValues,
                  [filterKey]: { type: `$${filterType}`, value: filterValue },
                });
                newVariables.filter = {
                  ...variables.filter,
                  [filterKey]: { type: `$${filterType}`, value: filterValue },
                };
              }
            });
          }
          return newVariables;
        });

        // Get cached page size and apply it
        const pageSizeQuery = query?.filter((q) => q.includes('limit='));
        if (pageSizeQuery?.length > 0) {
          const tempPageSize = parseInt(
            pageSizeQuery[0].replace('limit=', ''),
            10,
          );
          setPageSize(tempPageSize);
        }

        // Get cached page index and apply it
        const pageSkipQuery = query?.filter((q) => q.includes('skip='));
        if (pageSkipQuery?.length > 0) {
          const tempPageSkip = parseInt(
            pageSkipQuery[0].replace('skip=', ''),
            10,
          );

          const pageNumber = Math.floor(tempPageSkip / pageSize) + 1;
          setPageIndex(pageNumber - 1);
        }
      }
    }
    setFetchingPresets(false);

    // This is disabled because we don't want to re-run this function when any of the variables change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // USE EFFECTS
  useEffect(() => {
    getTablePresets();
  }, [getTablePresets]);

  useEffect(() => {
    if (refetch && !fetchingPresets) {
      setTimeout(() => {
        refetchData({
          variables,
          refetch,
          pageIndex,
          pageSize,
          sortBy,
          tableId,
        });
      }, 1);
    }
  }, [
    variables,
    refetch,
    pageIndex,
    pageSize,
    sortBy,
    setLoading,
    setError,
    tableId,
    fetchingPresets,
  ]);

  useEffect(() => {
    if (
      !busyDownloading &&
      downloadData.called &&
      !downloadData.loading &&
      downloadData.data
    ) {
      setBusyDownloading(true);
      startDownload();
      setDownloadData({
        called: false,
        loading: false,
        data: downloadData.data,
      });
    }

    if (busyDownloading) {
      Swal.fire(
        'Please wait!',
        'We are busy generating the report.',
        'warning',
      );
    }
  }, [downloadData, busyDownloading, startDownload, setDownloadData]);

  const renderSubRow = (row) => {
    if (row.isExpanded) {
      if (subRowSameHeaders) {
        return renderRowSubComponent({ row });
      }
      return (
        <tr>
          <td colSpan={visibleColumns.length}>
            {renderRowSubComponent({ row })}
          </td>
        </tr>
      );
    }
    return null;
  };

  const toggleContextMenu = (e) => {
    if (!isPopupOpen) {
      const buttonRect = e.target.getBoundingClientRect();
      const estimatedMenuWidth = 185; // Conservative estimate
      const estimatedMenuHeight = 160; // Conservative estimate for menu height

      const { alignment, x } = calculateHorizontalPosition(
        buttonRect,
        estimatedMenuWidth,
      );
      const { y, verticalPlacement } = calculateVerticalPosition(
        buttonRect,
        estimatedMenuHeight,
      );

      setPosition({ x, y, alignment, verticalPlacement });
      setIsPopupOpen(true);
    } else {
      setIsPopupOpen(false);
    }
  };

  return (
    <Main>
      {tableHeader && (
        <TableHeader
          filters={filters}
          variables={variables}
          setVariables={setVariables}
          tableTitle={tableTitle}
          refetch={refetch}
          setHiddenColumns={setHiddenColumns}
          tableOptions={tableOptions}
          tableHeaderProps={tableHeaderProps}
          additionalHeaderButtons={additionalHeaderButtons}
          defaultVariables={defaultVariables}
          columns={columns}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
          download={download}
          id={tableId}
          customClear={customClear}
          clearDefaultOnReset={clearDefaultOnReset}
        />
      )}
      <TableContainer style={{ maxHeight, ...bodyStyle }}>
        <StyledTable {...getTableProps()}>
          <thead
            style={{
              borderBottom: `2px solid ${theme.COLOR_TABLE_DARK}`,
            }}
          >
            {headerGroups.map((headerGroup) => (
              <RowHeader
                {...headerGroup.getHeaderGroupProps()}
                key={`header-group-${tableTitle}-${uuidv4()}`}
              >
                {headerGroup.headers.map((column) => {
                  if (column.id === 'expander') {
                    return (
                      <ColumnHeader
                        {...column.getHeaderProps(
                          column.getSortByToggleProps(),
                        )}
                        width={50}
                        key={`colheader-${uuidv4()}`}
                      >
                        <span>
                          {column.render('Header')}
                          {/* Add a sort direction indicator */}
                          {column.isSorted && column.isSortedDesc && (
                            <FiChevronDown />
                          )}
                          {column.isSorted && !column.isSortedDesc && (
                            <FiChevronUp />
                          )}
                        </span>
                      </ColumnHeader>
                    );
                  }

                  return (
                    <ColumnHeader
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      width={column.width || `${100 / columns.length}%`}
                      key={`colheader-${uuidv4()}`}
                    >
                      <span>
                        {column.render('Header')}
                        {/* Add a sort direction indicator */}
                        {column.isSorted && column.isSortedDesc && (
                          <FiChevronDown />
                        )}
                        {column.isSorted && !column.isSortedDesc && (
                          <FiChevronUp />
                        )}
                      </span>
                    </ColumnHeader>
                  );
                })}
              </RowHeader>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {loading && (
              <Row>
                <Column colSpan={columns.length} style={columnStyle}>
                  <Loader loading />
                </Column>
              </Row>
            )}
            {!loading && rows && error && (
              <Row>
                <Column colSpan={columns.length} style={columnPaddingStyle}>
                  <FiAlertTriangle
                    size={50}
                    color={theme.COLOR_TABLE_DARK}
                    strokeWidth={1.2}
                  />
                  <br />
                  <Text>
                    There was an error while loading your data, please try again
                  </Text>
                </Column>
              </Row>
            )}
            {!loading && !error && rows.length === 0 && (
              <Row>
                <Column colSpan={columns.length} style={columnPaddingStyle}>
                  <FiFileText
                    size={50}
                    color={theme.COLOR_TABLE_DARK}
                    strokeWidth={1.2}
                  />
                  <br />
                  <Text>There is currently no data to display</Text>
                </Column>
              </Row>
            )}
            {!loading &&
              !error &&
              rows.length > 0 &&
              rows.map((row) => {
                prepareRow(row);
                if (!row.id.includes('.')) {
                  return (
                    <Fragment key={`row-${tableTitle}-${uuidv4()}`}>
                      <Row
                        key={`row-${tableTitle}-${uuidv4()}`}
                        onClick={
                          onRowClick
                            ? () => {
                                onRowClick(row);
                              }
                            : null
                        }
                        onContextMenu={(e) => {
                          if (
                            row?.original &&
                            (onRowEdit ||
                              onRowDelete ||
                              onRowView ||
                              onRowPreview ||
                              onRowDuplicate)
                          ) {
                            e.preventDefault();
                            setSelectedRow(row);
                            toggleContextMenu(e);
                          } else {
                            setSelectedRow(null);
                          }
                        }}
                        className={onRowClick ? 'clickable' : ''}
                        {...row.getRowProps()}
                        style={
                          conditionRowStyle
                            ? conditionRowStyle(row.original)
                            : rowStyle
                        }
                      >
                        {row.cells.map((cell) => {
                          if (cell.column.id === 'expander') {
                            return (
                              <Column
                                height={50}
                                {...cell.getCellProps()}
                                colSpan={1}
                                width={
                                  cell.column.width ||
                                  `${100 / columns.length}%`
                                }
                                key={`row-${tableTitle}-${uuidv4()}-cell-${tableTitle}-${uuidv4()}`}
                              >
                                {cell.render('Cell')}
                              </Column>
                            );
                          }

                          return (
                            <Column
                              height={50}
                              {...cell.getCellProps()}
                              colSpan={1}
                              width={
                                cell.column.width || `${100 / columns.length}%`
                              }
                              key={`row-${tableTitle}-${uuidv4()}-cell-${tableTitle}-${uuidv4()}`}
                            >
                              {cell.render('Cell')}
                            </Column>
                          );
                        })}
                      </Row>
                      {renderSubRow(row)}
                    </Fragment>
                  );
                }
                return null;
              })}

            {selectedRow && isPopupOpen && (
              <ContextMenuViewEditDelete
                feature={feature}
                onEdit={onRowEdit && (() => onRowEdit?.(selectedRow))}
                onDelete={onRowDelete && (() => onRowDelete?.(selectedRow))}
                onView={onRowView && (() => onRowView?.(selectedRow))}
                onPreview={onRowPreview && (() => onRowPreview?.(selectedRow))}
                previewText={previewText}
                onDuplicate={
                  onRowDuplicate && (() => onRowDuplicate?.(selectedRow))
                }
                viewText={viewText}
                isPopupOpen={isPopupOpen}
                setIsPopupOpen={setIsPopupOpen}
                position={position}
              />
            )}
          </tbody>
        </StyledTable>
      </TableContainer>
      {isPaginationVisible && (
        <Pagination
          dataCount={dataCount}
          pageIndex={pageIndex}
          tableTitle={tableTitle}
          pageSizes={pageSizes}
          pageSize={pageSize}
          setPageSize={setPageSize}
          setPageIndex={setPageIndex}
        />
      )}
    </Main>
  );
};

Table.propTypes = {
  /**
   * The columns that the table should render. https://react-table.js.org/quickstart#define-columns
   */
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  /**
   * The data that should be rendered on the table rows. https://react-table.js.org/quickstart#define-row-shape
   */
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  /**
   * Flag if the data is loading, also displays loader on table if true..
   */
  loading: PropTypes.bool,
  /**
   * Flag if there was an error while loading data.
   */
  error: PropTypes.bool,
  /**
   * Query function to refetch the data, incase variables change or pagination changes.
   */
  refetch: PropTypes.func.isRequired,
  /**
   * The total number of data rows that there are.
   */
  dataCount: PropTypes.string,

  /**
   * Page sizes to display on the table. This is used to chunk the data
   * when quering from backend.
   */
  pageSizes: PropTypes.arrayOf(PropTypes.number),
  defaultPageSize: PropTypes.number,
  /**
   * An on click event that needs to trigger when a user clicks on a row.
   * If this is passed through there will be a hover on the row as well as the cursor will change.
   */
  onRowClick: PropTypes.func,

  tableTitle: PropTypes.string,
  tableOptions: PropTypes.arrayOf(
    PropTypes.oneOf(['search', 'download', 'columns', 'filter']),
  ),
  filters: PropTypes.arrayOf(PropTypes.shape({})),
  downloadData: PropTypes.arrayOf(PropTypes.shape({})),
  renderRowSubComponent: PropTypes.func,
  subRowSameHeaders: PropTypes.bool,
  getDownloadData: PropTypes.func,
  fileName: PropTypes.string,
  /**
   * MaxHeight of table, defaults to 400
   */
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Used for pagination in the custom graphql API.
   * The amount of pages that there are with the current pageSize.
   */
  defaultVariables: PropTypes.shape({}),
  multiSheet: PropTypes.bool,
  lazyExpander: PropTypes.bool,
  tableHeader: PropTypes.bool,
  emailExport: PropTypes.bool,
  modalExport: PropTypes.bool,
  getSheetName: PropTypes.func,
  setLoading: PropTypes.func,
  setData: PropTypes.func,
  setCount: PropTypes.func,
  setError: PropTypes.func,
  tableHeaderProps: PropTypes.element,
  rowStyle: PropTypes.shape({}),
  bodyStyle: PropTypes.shape({}),
  conditionRowStyle: PropTypes.func,
  setDownloadData: PropTypes.func,
  customClear: PropTypes.func,
  id: PropTypes.string.isRequired,
  defaultSort: PropTypes.string,
  isPaginationVisible: PropTypes.bool,
  additionalHeaderButtons: PropTypes.element,
  onRowContextMenu: PropTypes.func,
  feature: PropTypes.string,
  onRowEdit: PropTypes.func,
  onRowDelete: PropTypes.func,
  onRowDuplicate: PropTypes.func,
  onRowView: PropTypes.func,
  onRowPreview: PropTypes.func,
  previewText: PropTypes.string,
  viewText: PropTypes.string,
  clearDefaultOnReset: PropTypes.bool,
};

Table.defaultProps = {
  tableTitle: '',
  tableHeader: true,
  subRowSameHeaders: false,
  pageSizes: [50, 100, 150, 200],
  defaultPageSize: 50,
  loading: false,
  error: false,
  onRowClick: undefined,
  tableOptions: [],
  filters: [],
  dataCount: '0',
  downloadData: [],
  getDownloadData: undefined,
  fileName: '',
  maxHeight: '60vh',
  renderRowSubComponent: null,
  defaultVariables: {},
  multiSheet: false,
  lazyExpander: false,
  getSheetName: null,
  tableHeaderProps: undefined,
  setLoading: undefined,
  setData: undefined,
  setCount: undefined,
  setError: undefined,
  rowStyle: {},
  bodyStyle: {},
  conditionRowStyle: undefined,
  setDownloadData: undefined,
  customClear: undefined,
  defaultSort: undefined,
  emailExport: false,
  modalExport: false,
  isPaginationVisible: true,
  additionalHeaderButtons: undefined,
  onRowContextMenu: undefined,
  feature: undefined,
  onRowEdit: undefined,
  onRowDelete: undefined,
  onRowDuplicate: undefined,
  onRowView: undefined,
  onRowPreview: undefined,
  previewText: undefined,
  viewText: undefined,
  clearDefaultOnReset: false,
};

export { TableFilterIcons, TableHeader, TableIcons, TableTitle, TableWrapper };

export default Table;
